import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { tenant } from '@tenant';
import { default as Checkbox } from 'antd/es/checkbox';
import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import { default as Switch } from 'antd/es/switch';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { AuthMethodSegmented } from './AuthMethodSegmented';
import { CertificateUpload } from './CertificateUpload';
import {
  CREATE_KEY_ACKNOWLEDGE_LABEL,
  CREATE_KEY_ACKNOWLEDGE_TEST_ID,
  CREATE_KEY_CANCEL_LABEL,
  CREATE_KEY_CANCEL_TEST_ID,
  CREATE_KEY_DOCS_LINK_TEST_ID,
  CREATE_KEY_DOCUMENTATION_LABEL,
  CREATE_KEY_DOCUMENTATION_URL,
  CREATE_KEY_ENCRYPTION_HELPER,
  CREATE_KEY_ENCRYPTION_LABEL,
  CREATE_KEY_ENCRYPTION_TOGGLE_TEST_ID,
  CREATE_KEY_FORM_ID,
  CREATE_KEY_FULL_ACCESS_EMPHASIS,
  CREATE_KEY_FULL_ACCESS_PREFIX,
  CREATE_KEY_FULL_ACCESS_SUFFIX,
  CREATE_KEY_METHOD_LABEL,
  CREATE_KEY_MODAL_TEST_ID,
  CREATE_KEY_NAME_LABEL,
  CREATE_KEY_NAME_PLACEHOLDER,
  CREATE_KEY_NAME_TEST_ID,
  CREATE_KEY_REVEAL_TITLE,
  CREATE_KEY_SUBMIT_LABEL,
  CREATE_KEY_SUBMIT_TEST_ID,
  CREATE_KEY_SUPPORT_PREFIX,
  CREATE_KEY_SUPPORT_SUFFIX,
  CREATE_KEY_TITLE
} from './constants';
import { KeyCreatedPanel } from './KeyCreatedPanel';
import { useCreateKeyForm } from './useCreateKeyForm';

type CreateKeyModalProps = {
  open: boolean;
  onClose: () => void;
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <label className="mb-0.5 flex gap-0.5 text-xs font-medium text-gray-500">
    {children}
    <span className="text-feedback-error">*</span>
  </label>
);

export const CreateKeyModal = ({ open, onClose }: CreateKeyModalProps) => {
  const {
    form,
    submit,
    cancel,
    onFileSelected,
    removeUpload,
    setMethod,
    setEncryptionEnabled,
    createdScheme,
    isSubmitting,
    method,
    showEncryptionToggle,
    requiresUpload,
    uploadConfig
  } = useCreateKeyForm({ onClose });
  const {
    control,
    formState: { isValid }
  } = form;

  const encryptionEnabled = form.watch('encryptionEnabled');
  const upload = form.watch('upload');

  return (
    <Modal
      open={open}
      onCancel={cancel}
      width={520}
      destroyOnHidden
      title={
        <div className="flex items-center justify-between pr-6">
          <span className="text-xl font-medium">
            {createdScheme ? CREATE_KEY_REVEAL_TITLE : CREATE_KEY_TITLE}
          </span>
        </div>
      }
      footer={
        createdScheme ? null : (
          <div className="flex justify-end gap-2">
            <a
              data-testid={CREATE_KEY_DOCS_LINK_TEST_ID}
              href={CREATE_KEY_DOCUMENTATION_URL}
              target="_blank"
              rel="noreferrer"
              className="mr-auto flex cursor-pointer items-center gap-1 text-sm font-normal text-content-primary hover:underline"
            >
              {CREATE_KEY_DOCUMENTATION_LABEL}
              <ArrowTopRightOnSquareIcon className="size-4" />
            </a>
            <SecondaryButton
              data-testid={CREATE_KEY_CANCEL_TEST_ID}
              onClick={cancel}
            >
              {CREATE_KEY_CANCEL_LABEL}
            </SecondaryButton>
            <PrimaryButton
              data-testid={CREATE_KEY_SUBMIT_TEST_ID}
              form={CREATE_KEY_FORM_ID}
              htmlType="submit"
              disabled={!isValid || isSubmitting}
            >
              {CREATE_KEY_SUBMIT_LABEL}
            </PrimaryButton>
          </div>
        )
      }
    >
      {createdScheme ? (
        <KeyCreatedPanel scheme={createdScheme} onDone={cancel} />
      ) : (
        <form
          id={CREATE_KEY_FORM_ID}
          onSubmit={submit}
          data-testid={CREATE_KEY_MODAL_TEST_ID}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-content-secondary">
            {CREATE_KEY_SUPPORT_PREFIX}
            <a
              href={`mailto:${tenant.support.email}`}
              className="cursor-pointer underline hover:no-underline"
            >
              {tenant.support.email}
            </a>
            {CREATE_KEY_SUPPORT_SUFFIX}
          </p>

          <div className="flex flex-col">
            <FieldLabel>{CREATE_KEY_NAME_LABEL}</FieldLabel>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  data-testid={CREATE_KEY_NAME_TEST_ID}
                  placeholder={CREATE_KEY_NAME_PLACEHOLDER}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="h-12 rounded-2xl"
                />
              )}
            />
          </div>

          <div className="rounded-lg bg-surface-muted p-2 text-sm text-content-primary">
            {CREATE_KEY_FULL_ACCESS_PREFIX}
            <span className="font-semibold">
              {CREATE_KEY_FULL_ACCESS_EMPHASIS}
            </span>
            {CREATE_KEY_FULL_ACCESS_SUFFIX}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-base font-medium leading-[22px] text-content-primary">
              {CREATE_KEY_METHOD_LABEL}
            </p>
            <AuthMethodSegmented
              value={method}
              onChange={setMethod}
              requiresUpload={requiresUpload}
            />
          </div>

          {showEncryptionToggle && (
            <div className="flex items-center gap-3">
              <Switch
                checked={encryptionEnabled}
                onChange={setEncryptionEnabled}
                data-testid={CREATE_KEY_ENCRYPTION_TOGGLE_TEST_ID}
              />
              <div className="flex flex-col">
                <span className="text-base font-medium text-content-primary">
                  {CREATE_KEY_ENCRYPTION_LABEL}
                </span>
                <span className="text-sm text-content-secondary">
                  {CREATE_KEY_ENCRYPTION_HELPER}
                </span>
              </div>
            </div>
          )}

          {requiresUpload && (
            <CertificateUpload
              heading={uploadConfig.heading}
              formatsLabel={uploadConfig.formatsLabel}
              accept={uploadConfig.accept}
              upload={upload}
              onFileSelected={onFileSelected}
              onRemove={removeUpload}
            />
          )}

          <Controller
            name="acknowledge"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={!!field.value}
                onChange={() => field.onChange(!field.value)}
                data-testid={CREATE_KEY_ACKNOWLEDGE_TEST_ID}
              >
                <span className="text-sm text-content-primary">
                  {CREATE_KEY_ACKNOWLEDGE_LABEL}
                </span>
              </Checkbox>
            )}
          />
        </form>
      )}
    </Modal>
  );
};
